import {Button} from "@zendeskgarden/react-buttons";
import {Checkbox, Field, Hint, Label} from "@zendeskgarden/react-forms";
import {Inline} from "@zendeskgarden/react-loaders";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {UnorderedList} from "@zendeskgarden/react-typography";
import {ReactElement, useState} from "react";
import {toast} from "react-toastify";
import {ThemeProvider} from "styled-components";
import {CustomZDDropdown} from "../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import VZAlert from "../../../components/vz-ui/vz-alert/VZAlert";
import {COLORS} from "../../../constants/Colors";
import {ViziblyTheme} from "../../../styles/zendesk-garden/ViziblyZDGTheme";
import {pluralize} from "../../../utils/string-helpers";
import * as css from "./styles/copyOpDriverModal.module.scss";


interface CopyDriverModalSourceDriver {
    name: string;
    propertyId: string;
    propertyName: string;
    destinationAccountId: string;
    accountLabel: string;
    status: string;
    locked: boolean;
    valuesLocked: boolean;
}

export type CopyDriverModalOnSubmitArgs = {
    copyNote: boolean;
    copyStatus: boolean;
    selectedPropertyIds: string[];
    selectedAccountIds: string[];
}

export interface CopyDriverModalProps {
    visible: boolean;
    sourceDriver: CopyDriverModalSourceDriver;
    destinationProperties: {id: string; name: string, disabled?: boolean}[];
    defaultSelectedProperties?: string[];
    accounts: {id: string; label: string}[];
    note: string | undefined;
    noteLoading: boolean;
    copyBusy: boolean;
    onSubmit: (args: CopyDriverModalOnSubmitArgs) => Promise<boolean | undefined>;
    onClose: () => void;
    onCopied: () => void;
}

export function CopyDriverModal(props: CopyDriverModalProps): ReactElement {
    const {
        visible,
        sourceDriver,
        destinationProperties,
        defaultSelectedProperties,
        accounts,
        note,
        noteLoading,
        copyBusy,
        onSubmit,
        onClose,
        onCopied,
    } = props;

    const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>(defaultSelectedProperties ?? []);
    const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([sourceDriver.destinationAccountId]);
    const [confirmVisible, setConfirmVisible] = useState<boolean>(false);
    const [copyNote, setCopyNote] = useState(true);
    const [copyStatus, setCopyStatus] = useState(true);

    const showAccountSelection = accounts.length > 1;
    const destinationAccountIds = showAccountSelection ? selectedAccountIds : accounts.map(a => a.id);

    const canProceed = selectedPropertyIds.length > 0
        && destinationAccountIds.length > 0;

    const reset = () => {
        setSelectedPropertyIds([]);
        setSelectedAccountIds([]);
        setConfirmVisible(false);
        onClose();
    };

    function handleSumbit(args: CopyDriverModalOnSubmitArgs) {
        onSubmit(args).then(response => {
            if (response) {
                toast.success("Copied driver settings");
                onCopied();
                reset();
                return;
            }
            toast.error("Failed to copy driver settings");
        }).catch(() => {
            toast.error("Failed to copy driver settings");
        }).finally(() => {
            setConfirmVisible(false);
        });
    }

    if (!visible) {
        return <></>;
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Modal onClose={reset} className={css.modal}>
                <Header>
                    Copy Driver to Other Properties/Accounts
                </Header>
                <Body>
                    <p>Copy the driver <strong>"{sourceDriver.name} ({sourceDriver.accountLabel})"</strong>, as well as its assumption values, to other properties and accounts.</p>
                    <div className={css.formWrapper}>
                        <CustomZDDropdown
                            label="Destination Properties"
                            allOption
                            isMulti
                            options={destinationProperties.map(p => ({value: p.id, label: `${p.name}${p.disabled === true ? " - Locked" : ""}`, disabled: p.disabled ?? false}))}
                            initialSelectedItems={selectedPropertyIds}
                            applySelectedItems={setSelectedPropertyIds}
                            openDropdownPlaceholder="Search properties..."
                            closedDropdownPlaceholder="Select properties"
                            isError={selectedPropertyIds.length === 0}
                            errorMessage="Please select at least one property"
                            hint="Properties currently with this driver are selected by default"
                        />
                        {showAccountSelection && (
                            <div>
                                <CustomZDDropdown
                                    label="Destination Accounts"
                                    isMulti
                                    allOption
                                    options={accounts.map(a => ({value: a.id, label: a.label}))}
                                    initialSelectedItems={selectedAccountIds}
                                    applySelectedItems={setSelectedAccountIds}
                                    openDropdownPlaceholder="Search accounts..."
                                    closedDropdownPlaceholder="Select accounts"
                                    isError={selectedAccountIds.length === 0}
                                    errorMessage="Please select at least one account"
                                />
                            </div>
                        )}

                        <h5 className={css.additionalOptionsLabel}>Additional Copy Options</h5>
                        <Field>
                            <Checkbox defaultChecked checked={copyStatus} onChange={evt => setCopyStatus(evt.target.checked)}>
                                <Label>Copy Non-Admin Access Level</Label>
                                <Hint>Will set destination drivers to: "{sourceDriver.status}"</Hint>
                            </Checkbox>
                        </Field>
                        <Field>
                            <Checkbox
                                defaultChecked
                                checked={copyNote}
                                onChange={evt => setCopyNote(evt.target.checked)}
                                disabled={!note || note === "" || noteLoading}
                            >
                                <Label>Copy Driver Note</Label>
                                <Hint className={css.noteText}>
                                    {note ?
                                        `"${note}"`
                                        :
                                        "This driver does not have a note attached to it."
                                    }
                                </Hint>
                            </Checkbox>
                        </Field>
                        <VZAlert
                            message="If a driver with the same name exists on a destination property and account, it will overwrite existing data."
                            alertType="warning"
                            isCompact
                        />
                    </div>
                </Body>
                <Footer>
                    <FooterItem>
                        <Button onClick={reset} isBasic disabled={copyBusy}>
                            Cancel
                        </Button>
                    </FooterItem>
                    <FooterItem>
                        <Button
                            isPrimary
                            disabled={!canProceed || copyBusy}
                            onClick={() => setConfirmVisible(true)}
                        >
                            Copy
                        </Button>
                    </FooterItem>
                </Footer>
                <Close aria-label="Close modal"/>
            </Modal>
            {confirmVisible && (
                <Modal onClose={() => setConfirmVisible(false)}>
                    <Header>Confirm Mass Copy</Header>
                    <Body>
                        <p>
                            Are you sure you want to proceed with copying this driver to {selectedPropertyIds.length} {pluralize(selectedPropertyIds.length, "property", "properties")} and {selectedAccountIds.length} {pluralize(selectedAccountIds.length, "account")}?
                        </p>
                        <p>
                            <strong>The following will be overwritten for existing drivers:</strong>
                        </p>
                        <UnorderedList>
                            <UnorderedList.Item>Assumption Values</UnorderedList.Item>
                            {copyStatus &&
                                <UnorderedList.Item>Non-Admin Access Level</UnorderedList.Item>
                            }
                            {copyNote && typeof note === "string" && note.trim() !== "" &&
                                <UnorderedList.Item>Driver Note</UnorderedList.Item>
                            }
                        </UnorderedList>
                    </Body>
                    <Footer>
                        <FooterItem>
                            <Button onClick={() => setConfirmVisible(false)} isBasic disabled={copyBusy}>
                                Back
                            </Button>
                        </FooterItem>
                        <FooterItem>
                            <Button
                                isPrimary
                                onClick={() => {
                                    handleSumbit({
                                        copyNote,
                                        copyStatus,
                                        selectedPropertyIds,
                                        selectedAccountIds,
                                    });
                                }}
                                disabled={copyBusy}
                            >
                                {copyBusy
                                    ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading"/>
                                    : <span>Confirm Copy</span>
                                }
                            </Button>
                        </FooterItem>
                    </Footer>
                    <Close aria-label="Close confirmation modal"/>
                </Modal>
            )}
        </ThemeProvider>
    );
}
